import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ApiClient } from '../../api/client';
import type { ResourceCoordination } from './context';

/** Setup and diagnostics stay on the assistant page, outside the chat overlay. */
export default function ResourceNotifications({ status, ready, apiClient, refresh }: {
  status?: ResourceCoordination; ready: boolean; apiClient: ApiClient; refresh: () => Promise<void>;
}) {
  const operation = useRef<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const verified = status?.idle_wake_qualified === true;
  const terminal = status?.delivery?.delivery_id === operation.current
    && ['complete', 'unsupported'].includes(status.delivery.state);
  const checkOnly = operation.current !== null && !terminal;
  const test = async () => {
    setBusy(true);
    try {
      if (checkOnly) { await refresh(); return; }
      operation.current = crypto.randomUUID();
      const result = await apiClient.post<{ prepared: boolean }>('/api/assistant/gateway/resources/probe', { operation_id: operation.current });
      if (result.prepared) setMessage('The test will run when your assistant finishes its current reply.');
      else { operation.current = null; setMessage('Notifications are not ready yet. Open your assistant and ask it about shared resources, then try again.'); }
      await refresh();
    } catch { setMessage('The test could not be confirmed. Check its saved status before starting another.'); }
    finally { setBusy(false); }
  };
  return <section className="personal-gateway-card" aria-labelledby="assistant-coordination-title">
    <h2 id="assistant-coordination-title">Shared resources</h2>
    <p>Your assistant can coordinate shared resources and hear when they become available.</p>
    {verified ? <p role="status">Notifications verified. Your assistant can receive updates while idle.</p>
      : <p>Your assistant keeps checking while it is working. Idle notifications have not been verified yet.</p>}
    <Link to="/resources" className="assistant-sources-link">View shared resources</Link>
    {status?.registered && <p>A notification test uses your selected model.</p>}
    {status?.registered && <button disabled={!ready || busy} onClick={() => { void test(); }}>
      {busy ? 'Requesting test…' : checkOnly ? 'Check test status' : verified ? 'Test notifications again' : 'Test notifications'}
    </button>}
    {(checkOnly || !verified) && message && <p role="status">{message}</p>}
    {!verified && terminal && status?.delivery?.state === 'unsupported' && <p>The test did not start. Try again while your assistant is idle.</p>}
  </section>;
}
